import axios from "axios";
import { LOCAL_STORAGE, getDataSession } from "helper/session";
import { getCmsToken } from "helper/user";
import _ from "lodash";
import { Option } from "modules/UAA/configs/constant";
import { LIST_LANGUAGUE } from "theme/layout/LanguageButton";
import { errorMsg, errorMsgVss } from "../helper/message";
import Logger from "./axios-vss-logger";
import i18n from "../i18n";
import { notifyError } from "../helper/system-notify"
const axiosInstance = axios.create();

const { t } = i18n;

// const baseApiEndpoint = `${process.env.REACT_APP_API_ENDPOINT}`;
const baseApiEndpoint = '';

axiosInstance.interceptors.request.use(
  (config) => {
    Logger.describeRequest(config);
    const token = getCmsToken();
    const languageCode = getDataSession(LOCAL_STORAGE, "languageCode");
    config.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
    config.headers["Pragma"] = "no-cache";
    config.headers["Expires"] = 0;

    const hasToken = Object.keys(token).length > 0;

    if (languageCode) {
      const currentLang = LIST_LANGUAGUE.find(
        (item) => item.code.toLocaleLowerCase() === languageCode
      );
      config.headers["Accept-Language"] = currentLang.codeHeader;
    } else {
      config.headers["Accept-Language"] = "en-US";
    }

    if (hasToken) {
      config.headers["Authorization"] = `Bearer ${_.get(
        token,
        "access_token"
      )}`;
    }

    return config;
  },
  (error) => {
    notifyError(error)
    Logger.describeErrorResponse(error);
    Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  // @ts-ignore
  async function (response) {
    Logger.describeSuccessResponse(response);
    return handleResponse(response);
  },
  async function (error) {
    notifyError(error)
    Logger.describeErrorResponse(error);

    return handleError(error);
  }
);

export default axiosInstance;

const handleResponse = async (response) => {
  const { data, status: statusCode, headers: header } = response || {};
  const { errorCodes = data?.data?.errorCodes } = data || {};
  const isSuccess =
    _.isNil(errorCodes) ||
    _.isEmpty(errorCodes) ||
    _.isEqual(_.first(errorCodes)?.message, "Success");
  if (!isSuccess) {
    errorMsgVss(errorCodes || t(`message.sys-error`));
    notifyError(response)
  }

  return Promise.resolve({
    data,
    statusCode,
    isSuccess,
    header,
  });
};

const handleError = (error) => {
  const userInfor = getDataSession(LOCAL_STORAGE, "user");
  const statusCode = error?.response?.status;
  const errorData = error?.response?.data;

  if (statusCode === 401 && errorData?.error === "invalid_token") {
    localStorage.clear();
    sessionStorage.clear();
    window.location.replace("/login");
    return;
  }
  notifyError(error)
  if (statusCode === 500) {
    errorMsg(t(`message.sys-busy`));
  }

  if (error?.code === "ERR_NETWORK") {
    errorMsg(error.message ?? t(`message.net-error`));
  }

  const msgError =
    !!error &&
    error?.response?.status === 403 &&
    // @ts-ignore
    t(`message.user-dont-transaction`, { username: userInfor?.username });
  // `Your user account ${userInfor?.username} is not allow to do this transaction. Please check again`;
  errorMsg(msgError);

  return Promise.resolve({
    data: error?.response?.data,
    statusCode: error?.response?.status,
    isSuccess: false,
    error_message: msgError,
  });
};

export class APIChannelService {
  axiosApi = axiosInstance;

  constructor(axiosApi) {
    if (axiosApi) {
      this.axiosApi = axiosApi;
    }
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
  }

  get = (url, params, config) => {
    let response;
    if (params && Object.keys(params).length !== 0) {
      const queryString = params
        ? Object.keys(params)
            .map((key) => key + "=" + params[key])
            .join("&")
        : "";

      response = this.axiosApi.get(`${baseApiEndpoint}${url}?${queryString}`, config);
    } else {
      response = this.axiosApi.get(`${baseApiEndpoint}${url}`, config);
    }
    return response;
  };
  post = (url, data, config) => {
    return this.axiosApi.post(`${baseApiEndpoint}${url}`, data, config);
  };

  put = (url, data, config) => {
    return this.axiosApi.put(`${baseApiEndpoint}${url}`, data, config);
  };
  delete = (url, data) => {
    return this.axiosApi.delete(`${baseApiEndpoint}${url}`, data);
  };
  postParam = (url, params) => {
    const queryString = params
      ? Object.keys(params)
          .map((key) => key + "=" + params[key])
          .join("&")
      : "";

    return this.axiosApi.post(`${baseApiEndpoint}${url}?${queryString}`);
  };
  postMultiForm = (url, data) => {
    const config = {
      headers: { "Content-Type": "multipart/form-data" },
    };
    return this.axiosApi.post(`${baseApiEndpoint}${url}`, data, config);
  };
  postDownloadFile = (url, data, config) => {
    return this.axiosApi.post(`${baseApiEndpoint}${url}`, data, { ...config, responseType: "blob" });
  };
  getDownloadFile = (url, data, config) => {
    return this.get(`${baseApiEndpoint}${url}`, data, {
      ...config,
      responseType: "blob",
    });
  };
  postFilter = (url, payload) => {
    return this.axiosApi.post(`${baseApiEndpoint}${url}`, payload);
  };
  getFilter = (url, payload) => {
    const { page = 0, size = 10 } = payload || {};
    return this.get(`${baseApiEndpoint}${url}`, { ...payload, page, size });
  };
  postApprove = (url, payload) => {
    return this.axiosApi.post(`${baseApiEndpoint}${url}`, payload, {
      headers: { TRANS_ID: payload.transactionId },
    });
  };
  postSearch = (url, payload) => {
    Object.keys(payload).forEach((key) => {
      if (payload[key] === Option.ALL || payload[key] === "") {
        payload[key] = null;
      }
    });

    const { page = 0, size = 10 } = payload || {};
    return this.axiosApi.post(
      `${baseApiEndpoint}${url}?page=${page}&size=${size}`,
      _.omit(payload, ["page", "size"])
    );
  };
}
